import React, { useEffect, useState, useMemo } from 'react';
import { useKanban } from '../context/KanbanContext';

interface PoliticasPanelProps {
    onClose: () => void;
}

interface Seccion {
    titulo: string;
    contenido: string;
}

function parsearPoliticas(blob: string): Seccion[] {
    if (!blob) return [];
    const lineas = blob.split('\n');
    const secciones: Seccion[] = [];
    let actual: Seccion | null = null;
    for (const linea of lineas) {
        const match = linea.match(/^\*?\*?(.+?)\*?\*?:?\s*$/);
        const esTitulo = match && linea.trim().endsWith(':') && linea.trim().length < 80
            && !linea.trim().startsWith('*') && linea === linea.toUpperCase() === false
            && /^[A-ZÁÉÍÓÚÑ]/.test(linea.trim());
        if (esTitulo && match) {
            if (actual) secciones.push(actual);
            actual = { titulo: linea.replace(/[:*]/g, '').trim(), contenido: '' };
        } else if (actual) {
            actual.contenido += (actual.contenido ? '\n' : '') + linea;
        } else {
            secciones.push({ titulo: 'General', contenido: linea });
            actual = null;
        }
    }
    if (actual) secciones.push(actual);
    return secciones.filter(s => s.contenido.trim() || s.titulo);
}

export const PoliticasPanel: React.FC<PoliticasPanelProps> = ({ onClose }) => {
    const { politicas, fetchPoliticas, actualizarPoliticas } = useKanban();
    const [loading, setLoading] = useState(true);
    const [editando, setEditando] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [contenido, setContenido] = useState('');
    const [seccionActiva, setSeccionActiva] = useState<number>(0);

    useEffect(() => {
        (async () => {
            setLoading(true);
            await fetchPoliticas();
            setLoading(false);
        })();
    }, [fetchPoliticas]);

    useEffect(() => {
        if (politicas) setContenido(politicas.contenido);
    }, [politicas]);

    const secciones = useMemo(() => parsearPoliticas(contenido), [contenido]);

    const handleEditar = () => {
        setContenido(politicas?.contenido || '');
        setEditando(true);
    };

    const handleGuardar = async () => {
        setGuardando(true);
        const success = await actualizarPoliticas(contenido);
        setGuardando(false);
        if (success) setEditando(false);
    };

    const renderContenidoLectura = () => {
        if (secciones.length === 0) {
            return <p className="text-sm text-slate-500 italic">Sin contenido</p>;
        }
        return (
            <div className="space-y-4">
                {secciones.map((sec, idx) => (
                    <div key={idx} className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                        <h4 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-2">
                            <span className="w-6 h-6 bg-slate-800 text-white rounded-full flex items-center justify-center text-xs">
                                {idx + 1}
                            </span>
                            {sec.titulo}
                        </h4>
                        <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed pl-8">
                            {sec.contenido.trim()}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col transform transition-all"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-5 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center text-white text-lg">
                            ℹ️
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-slate-800">Políticas del Tablero</h2>
                            <p className="text-xs text-slate-500">
                                {editando ? 'Editando como texto libre (Markdown)' : `${secciones.length} sección(es) detectadas automáticamente`}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="p-5 overflow-y-auto flex-1">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-32 gap-3">
                            <div className="relative">
                                <div className="w-10 h-10 border-4 border-slate-200 rounded-full"></div>
                                <div className="absolute inset-0 border-4 border-transparent border-t-blue-500 rounded-full animate-spin"></div>
                            </div>
                            <p className="text-slate-500 text-sm">Cargando políticas...</p>
                        </div>
                    ) : editando ? (
                        <div>
                            <div className="mb-2 text-xs text-slate-500">
                                💡 Las secciones se detectan automáticamente por líneas que terminan en `:` y empiezan con mayúscula.
                            </div>
                            <textarea
                                value={contenido}
                                onChange={(e) => setContenido(e.target.value)}
                                rows={22}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none font-mono text-sm"
                            />
                        </div>
                    ) : (
                        renderContenidoLectura()
                    )}
                </div>

                <div className="flex items-center justify-between p-5 border-t border-slate-100 bg-slate-50/50 rounded-b-2xl">
                    <div className="text-xs text-slate-500">
                        {secciones.length > 0 && !editando && (
                            <>Última edición: {politicas?.actualizado_en ? new Date(politicas.actualizado_en).toLocaleString('es-AR') : '—'}</>
                        )}
                    </div>
                    <div className="flex gap-2">
                        {editando ? (
                            <>
                                <button
                                    onClick={() => setEditando(false)}
                                    className="px-4 py-2 text-sm border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-100 transition-colors font-medium"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleGuardar}
                                    disabled={guardando}
                                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium disabled:opacity-50"
                                >
                                    {guardando ? 'Guardando...' : 'Guardar'}
                                </button>
                            </>
                        ) : (
                            <>
                                {!loading && (
                                    <button
                                        onClick={handleEditar}
                                        className="px-4 py-2 text-sm bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium"
                                    >
                                        Editar
                                    </button>
                                )}
                                <button
                                    onClick={onClose}
                                    className="px-4 py-2 text-sm border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-100 transition-colors font-medium"
                                >
                                    Cerrar
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
